import { parseFile } from "@/services/fileParser";
import type { ValidationResult } from "@/types/schemaBuilder";
import { cn, formatBytes } from "@/lib/utils";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileSpreadsheet,
  Info,
  Loader2,
  RefreshCw,
  RotateCcw,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

type ModalView = "upload" | "confirm" | "running" | "results" | "error";
type RunStep = "saving" | "running";

// the actual backend phases of the save-and-validate call, not invented ones
const RUN_STEPS: Array<{ key: RunStep; label: string }> = [
  { key: "saving", label: "Saving draft" },
  { key: "running", label: "Running validation rules" },
];

// purely cosmetic pacing for the first step; the real call resolves both steps
const STEP_DELAYS_MS: Record<"saving", number> = {
  saving: 600,
};

const VALIDATION_NOTE =
  "Columns are matched to the schema by header name — make sure the file's column headers match the schema's column names.";

const MAX_VALIDATE_FILE_MB = 25;

interface ValidateDraftModalProps {
  onValidate: (file: File) => Promise<ValidationResult>;
  onClose: () => void;
  onPublish: () => void;
  onEditRule: (ruleId: string) => void;
  /** schema.publish permission (App.ADMIN) — hides the Publish button when false. */
  canPublish: boolean;
  /** Warnings from the accompanying draft save. */
  saveWarnings?: string[];
  /** Conflict messages from the accompanying draft save. */
  conflictMessages?: string[];
}

interface ParsedFileInfo {
  rowCount: number;
  columnCount: number;
}

export function ValidateDraftModal({
  onValidate,
  onClose,
  onPublish,
  onEditRule,
  canPublish,
  saveWarnings = [],
  conflictMessages = [],
}: ValidateDraftModalProps) {
  const [view, setView] = useState<ModalView>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileInfo, setFileInfo] = useState<ParsedFileInfo | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<RunStep>>(new Set());
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [expandedRuleIds, setExpandedRuleIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  function toggleExpandedSample(ruleId: string) {
    setExpandedRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  }

  // The "running" step completes, and the view advances, only once onValidate resolves.
  useEffect(() => {
    if (view !== "running" || !file) return;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    (Object.entries(STEP_DELAYS_MS) as Array<[RunStep, number]>).forEach(([step, delay]) => {
      timers.push(
        setTimeout(() => {
          if (!cancelled) setCompletedSteps((prev) => new Set([...prev, step]));
        }, delay)
      );
    });

    void onValidate(file)
      .then((result) => {
        if (cancelled) return;
        setCompletedSteps(new Set(["saving", "running"]));
        setValidationResult(result);
        setView("results");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRunError(err instanceof Error ? err.message : "Validation failed. Please try again.");
        setView("error");
      });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  function validateAndSetFile(selected: File) {
    if (selected.size > MAX_VALIDATE_FILE_MB * 1024 * 1024) {
      setUploadError(`File exceeds ${MAX_VALIDATE_FILE_MB} MB limit.`);
      return;
    }
    const ext = selected.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      setUploadError("Accepts .xlsx, .xls, .csv files only.");
      return;
    }
    setUploadError("");
    setFile(selected);
    setIsParsing(true);

    void parseFile(selected)
      .then((worksheets) => {
        const sheet = worksheets[0];
        setFileInfo({
          rowCount: sheet?.totalRows ?? 0,
          columnCount: sheet?.headers.length ?? 0,
        });
      })
      .catch(() => {
        setFileInfo({ rowCount: 0, columnCount: 0 });
      })
      .finally(() => {
        setIsParsing(false);
        setView("confirm");
      });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    if (selected) validateAndSetFile(selected);
    e.target.value = "";
  }

  function handleChangeFile() {
    setFile(null);
    setFileInfo(null);
    setUploadError("");
    setView("upload");
  }

  function handleRunValidation() {
    setCompletedSteps(new Set());
    setValidationResult(null);
    setRunError(null);
    setView("running");
  }

  function handleTestAnotherFile() {
    setFile(null);
    setFileInfo(null);
    setValidationResult(null);
    setCompletedSteps(new Set());
    setRunError(null);
    setView("upload");
  }

  const stepIndex =
    view === "confirm" ? 1 : view === "running" || view === "results" || view === "error" ? 2 : 0;

  const isRunning = view === "running";
  // Hold the bar one segment back while running so it doesn't read as complete alongside the spinner.
  const barStepIndex = isRunning ? stepIndex - 1 : stepIndex;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div
        className={cn(
          "relative flex max-h-[90vh] flex-col rounded-lg bg-white shadow-2xl",
          view === "results" ? "w-[600px]" : "w-[520px]"
        )}
      >
        <div className="shrink-0 px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                {view === "results" ? "Validation results" : "Test this schema"}
              </h2>
              <p className="mt-0.5 truncate text-sm text-[var(--color-text-secondary)]">
                {view === "results" && validationResult
                  ? `${validationResult.schemaName} · ${validationResult.fileName}`
                  : "Upload a sample file to run validation rules against"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {view === "results" && (
                <button
                  type="button"
                  onClick={handleRunValidation}
                  className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-[var(--color-border)] bg-white px-3 py-1.5 text-sm font-semibold text-[var(--color-text-primary)] transition hover:bg-[var(--color-bg-secondary)]"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Re-run validation
                </button>
              )}
              {!isRunning && (
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-0.5 shrink-0 rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {view !== "results" && (
            <div className="mt-4">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-1 flex-1 rounded-full transition-colors",
                      i <= barStepIndex
                        ? "bg-[var(--color-action-primary)]"
                        : "bg-[var(--color-border)]"
                    )}
                  />
                ))}
              </div>
              <p className="mt-2 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]">
                {view === "upload" && "Step 1 of 3 · Upload file"}
                {view === "confirm" && "Step 2 of 3 · Confirm file"}
                {view === "running" && "Step 3 of 3 · Running validation"}
                {view === "error" && "Step 3 of 3 · Validation failed"}
              </p>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {view === "upload" && (
            <>
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={cn(
                  "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 text-center transition-colors",
                  isDragging
                    ? "border-[var(--color-action-primary)] bg-green-50"
                    : "border-[var(--color-border)] hover:border-[var(--color-action-primary)] hover:bg-[var(--color-bg-secondary)]"
                )}
              >
                <Upload className="h-9 w-9 text-[var(--color-text-tertiary)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    Drag and drop a file here
                  </p>
                  <p className="mt-0.5 text-sm text-[var(--color-text-tertiary)]">or</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  className="rounded border border-[var(--color-border)] bg-white px-4 py-1.5 text-sm text-[var(--color-text-primary)] transition hover:bg-[var(--color-bg-secondary)]"
                >
                  Browse files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleInputChange}
                  className="sr-only"
                />
              </div>

              {uploadError && <p className="mt-2 text-sm text-red-600">{uploadError}</p>}

              <p className="mt-3 text-xs text-[var(--color-text-tertiary)]">
                Accepts .xlsx, .xls, .csv · Max {MAX_VALIDATE_FILE_MB} MB
              </p>
              <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <p className="text-xs text-blue-700">{VALIDATION_NOTE}</p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)]"
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {view === "confirm" && file && (
            <>
              <div className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] p-4">
                <FileSpreadsheet className="h-8 w-8 shrink-0 text-[var(--color-brand-primary)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
                    {file.name}
                  </p>
                  <p className="text-xs text-[var(--color-text-tertiary)]">
                    {formatBytes(file.size)}
                    {fileInfo && fileInfo.rowCount > 0 && ` · ${fileInfo.rowCount} rows detected`}
                    {fileInfo && fileInfo.columnCount > 0 && ` · ${fileInfo.columnCount} columns`}
                    {isParsing && " · Reading…"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleChangeFile}
                  className="shrink-0 rounded p-1 text-[var(--color-text-tertiary)] transition hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <p className="text-xs text-blue-700">{VALIDATION_NOTE}</p>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleChangeFile}
                  className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change file
                </button>
                <button
                  type="button"
                  disabled={isParsing}
                  onClick={handleRunValidation}
                  className="rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Run validation
                </button>
              </div>
            </>
          )}

          {view === "running" && (
            <div className="py-4">
              <div className="space-y-0 divide-y divide-[var(--color-border-light)]">
                {RUN_STEPS.map(({ key, label }) => {
                  const done = completedSteps.has(key);
                  return (
                    <div key={key} className="flex items-center gap-3 py-3.5">
                      {done ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-[var(--color-action-primary)]" />
                      ) : (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[var(--color-text-tertiary)]" />
                      )}
                      <span
                        className={cn(
                          "text-sm",
                          done
                            ? "text-[var(--color-text-primary)]"
                            : "text-[var(--color-text-secondary)]"
                        )}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 overflow-hidden rounded-full bg-[var(--color-border-light)]">
                <div className="h-0.5 animate-pulse bg-[var(--color-action-primary)]" />
              </div>
              <p className="mt-4 text-center text-xs text-[var(--color-text-tertiary)]">
                This usually takes a few seconds…
              </p>
            </div>
          )}

          {view === "error" && (
            <>
              <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <div>
                  <p className="text-sm font-semibold text-red-700">Couldn't run validation</p>
                  <p className="mt-0.5 text-sm text-red-600">
                    {runError ?? "Something went wrong. Please try again."}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleChangeFile}
                  className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change file
                </button>
                <button
                  type="button"
                  onClick={handleRunValidation}
                  className="rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)]"
                >
                  Try again
                </button>
              </div>
            </>
          )}

          {view === "results" && validationResult && (
            <>
              {(saveWarnings.length > 0 || conflictMessages.length > 0) && (
                <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <p className="mb-1 font-semibold">
                    The draft save behind this validation had issues:
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    {saveWarnings.map((w, i) => (
                      <li key={`w-${i}`}>{w}</li>
                    ))}
                    {conflictMessages.map((c, i) => (
                      <li key={`c-${i}`}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validationResult.failed > 0 ? (
                <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div>
                    <p className="text-sm font-semibold text-red-700">Validation Complete</p>
                    <p className="mt-0.5 text-sm text-red-600">
                      {validationResult.failed} of {validationResult.rulesTested} rule checks failed
                      across {validationResult.rowsTested} rows. Review the issues below before
                      publishing.
                    </p>
                  </div>
                </div>
              ) : validationResult.skipped > 0 ? (
                <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-700">
                      {validationResult.skipped} rule check
                      {validationResult.skipped === 1 ? "" : "s"} couldn't be run
                    </p>
                    <p className="mt-0.5 text-sm text-amber-600">
                      {validationResult.passed} of {validationResult.rulesTested} checks passed
                      across {validationResult.rowsTested} rows. The rest reference a column that
                      wasn't found in this file, or need the full dataset — review before
                      publishing.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-green-700">All checks passed</p>
                    <p className="mt-0.5 text-sm text-green-600">
                      {validationResult.rulesTested} of {validationResult.rulesTested} checks passed
                      across {validationResult.rowsTested} rows. This schema is ready to publish.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 grid grid-cols-5 gap-4 border-b border-[var(--color-border-light)] pb-5">
                <StatCell label="Rows tested" value={validationResult.rowsTested} />
                <StatCell label="Rules tested" value={validationResult.rulesTested} />
                <StatCell
                  label="Passed"
                  value={validationResult.passed}
                  colorClass="text-green-600"
                />
                <StatCell
                  label="Failed"
                  value={validationResult.failed}
                  colorClass={validationResult.failed > 0 ? "text-red-600" : undefined}
                />
                <StatCell
                  label="Skipped"
                  value={validationResult.skipped}
                  colorClass={validationResult.skipped > 0 ? "text-amber-600" : undefined}
                />
              </div>

              <div className="mt-4 space-y-3">
                {[
                  ...validationResult.ruleResults.filter(
                    (r) => r.status === "evaluated" && !r.passed
                  ),
                  ...validationResult.ruleResults.filter((r) => r.status !== "evaluated"),
                  ...validationResult.ruleResults.filter(
                    (r) => r.status === "evaluated" && r.passed
                  ),
                ].map((ruleResult) =>
                  ruleResult.status !== "evaluated" ? (
                    <div
                      key={ruleResult.ruleId}
                      className="flex items-center gap-3 rounded border border-amber-200 bg-amber-50 px-4 py-3"
                    >
                      <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                      <p className="text-sm text-[var(--color-text-primary)]">
                        {!ruleResult.isMultiColumn && (
                          <span className="font-medium">{ruleResult.columnName} · </span>
                        )}
                        <span className="font-medium">{ruleResult.ruleType}</span>
                        <span className="text-[var(--color-text-secondary)]">
                          {" "}
                          — {ruleResult.summary}
                        </span>
                      </p>
                    </div>
                  ) : ruleResult.passed ? (
                    <div
                      key={ruleResult.ruleId}
                      className="flex items-center gap-3 rounded border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-4 py-3"
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                      <p className="text-sm text-[var(--color-text-primary)]">
                        {!ruleResult.isMultiColumn && (
                          <span className="font-medium">{ruleResult.columnName} · </span>
                        )}
                        <span className="font-medium">{ruleResult.ruleType}</span>
                        <span className="text-[var(--color-text-secondary)]">
                          {" "}
                          — {ruleResult.summary}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div
                      key={ruleResult.ruleId}
                      className="rounded border border-red-200 bg-red-50 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                          <div>
                            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                              {ruleResult.isMultiColumn
                                ? ruleResult.ruleType
                                : `${ruleResult.columnName} · ${ruleResult.ruleType}`}
                            </p>
                            <p className="mt-0.5 text-sm text-red-600">{ruleResult.summary}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => onEditRule(ruleResult.ruleId)}
                          className="shrink-0 rounded border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] transition hover:bg-[var(--color-bg-secondary)]"
                        >
                          Edit rule
                        </button>
                      </div>

                      {ruleResult.failingSample && ruleResult.failingSample.length > 0 && (
                        <div className="mt-3 rounded border border-red-100 bg-white">
                          <div className="grid grid-cols-[24px_1fr] gap-5 border-b border-red-100 px-3 py-1.5 text-xs font-medium text-[var(--color-text-tertiary)]">
                            <span>#</span>
                            <span>Value</span>
                          </div>
                          {(expandedRuleIds.has(ruleResult.ruleId)
                            ? ruleResult.failingSample
                            : ruleResult.failingSample.slice(0, 3)
                          ).map((sample) => (
                            <div
                              key={sample.sampleIndex}
                              className="grid grid-cols-[24px_1fr] items-start gap-5 border-b border-red-100 px-3 py-1.5 text-sm last:border-b-0"
                            >
                              <span className="text-[var(--color-text-tertiary)]">
                                {sample.sampleIndex}
                              </span>
                              <div className="min-w-0 space-y-0.5">
                                {sample.values.map((v) => (
                                  <div
                                    key={v.column}
                                    className="truncate text-[var(--color-text-primary)]"
                                  >
                                    {sample.values.length > 1 && (
                                      <span className="text-[var(--color-text-tertiary)]">
                                        {v.column}:{" "}
                                      </span>
                                    )}
                                    {v.value || "(blank)"}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {ruleResult.failingSample.length > 3 && (
                            <button
                              type="button"
                              onClick={() => toggleExpandedSample(ruleResult.ruleId)}
                              className="w-full px-3 py-1.5 text-left text-xs font-medium text-[var(--color-action-primary)] transition hover:underline"
                            >
                              {expandedRuleIds.has(ruleResult.ruleId)
                                ? "Show less"
                                : `+ ${ruleResult.failingSample.length - 3} more`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleTestAnotherFile}
                  className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)]"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Test another file
                </button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-[var(--color-action-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-action-primary)] transition hover:bg-green-50"
                  >
                    Continue Editing
                  </button>
                  {canPublish && (
                    <button
                      type="button"
                      onClick={onPublish}
                      className="rounded-md bg-[var(--color-action-primary)] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)]"
                    >
                      Publish Schema
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({
  label,
  value,
  colorClass,
}: {
  label: string;
  value: number;
  colorClass?: string;
}) {
  return (
    <div>
      <p className="text-xs text-[var(--color-text-tertiary)]">{label}</p>
      <p className={cn("mt-0.5 text-2xl font-bold text-[var(--color-text-primary)]", colorClass)}>
        {value}
      </p>
    </div>
  );
}
